// ---------------------------------------------------------------------------
// Medication domain model — versions, schedules, doses and the change log.
//
// A medication is never edited in place and never deleted: every change adds a
// new VERSION with the date and time it takes effect, so a dose recorded weeks
// ago still reads against the schedule that was in force when it was given.
// `medication_plan` (a JSON column) holds:
//
//   [{ id, versions: [version, ...], changes: [change, ...] }]
//
// and a version is the whole prescription at a point in time:
//
//   { name, strength, dose, route, category, instruction, frequency, status,
//     times: ['09:00'], days: ['Mon'...], start, end, doctor, notes, prn,
//     effectiveFrom: 'YYYY-MM-DDTHH:mm', supersedesVersions: [index...] }
//
// Medications saved before versioning are plain objects with the fields at the
// top level — normalizeMedicine() wraps one as a single version that has always
// been in force, so old and new records read through exactly the same helpers.
// ---------------------------------------------------------------------------

import { medTimes, frequencyFromTimes, normalizeTimeList } from './medicationTimes';

export const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const ROUTE_OPTIONS = [
    'Oral', 'Topical', 'Inhaled', 'Eye', 'Ear', 'Nasal',
    'Subcutaneous', 'Intramuscular', 'Transdermal', 'Rectal',
    'Other — see instructions',
];

export const VERSION_STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'hold', label: 'On hold' },
];

// The lifecycle wording the mobile app's medical-history screen still reads out
// of `medication_history`. Kept in step with the version status on every save.
export const LIFECYCLE_FOR_STATUS = { active: 'Inprogress', hold: 'Stopped', archived: 'Completed' };
const STATUS_FOR_LIFECYCLE = { Inprogress: 'active', Stopped: 'hold', Completed: 'archived' };

// --- dates -----------------------------------------------------------------

// Parsed at UTC noon so a date never slides a day either side of midnight.
export const parseDate = (iso) => new Date(`${iso}T12:00:00Z`);
export const dateISO = (date) => date.toISOString().slice(0, 10);

const pad = (n) => String(n).padStart(2, '0');

export const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 'YYYY-MM-DDTHH:mm' in local time — the stamp format versions are keyed on. */
export const nowStamp = () => {
    const d = new Date();
    return `${todayISO()}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fmtDate = (iso, full = false) => {
    if (!iso) return '—';
    const value = String(iso).slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return String(iso);
    return parseDate(value).toLocaleDateString('en-GB', full
        ? { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }
        : { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

export const fmtMonth = (date) =>
    date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** '2026-09-14T09:00' -> '14 Sep 2026 at 09:00' */
export const fmtStamp = (stamp) => {
    if (!stamp) return '—';
    return `${fmtDate(stamp.slice(0, 10))} at ${stamp.slice(11, 16)}`;
};

/** A DB timestamp -> 'HH:mm' in local time. */
export const fmtClock = (value) => {
    if (!value) return '';
    try { return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

export const dayName = (iso) => DAYS[(parseDate(iso).getUTCDay() + 6) % 7];

/** Days between two ISO dates, inclusive of both ends. */
export const courseLength = (start, end) =>
    Math.round((parseDate(end) - parseDate(start)) / 86400000) + 1;

// --- normalising -----------------------------------------------------------

export const EMPTY_VERSION = {
    name: '', strength: '', dose: '', route: '', category: '', instruction: '',
    frequency: 'once', status: 'active', times: ['09:00'], days: [...DAYS],
    start: '', end: '', doctor: '', notes: '', prn: null,
    effectiveFrom: '', supersedesVersions: [],
};

const asArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        try { const parsed = JSON.parse(value); return Array.isArray(parsed) ? parsed : []; }
        catch { return []; }
    }
    return [];
};

export const parsePlan = asArray;

/**
 * A saved medication in its versioned form. Pre-versioning records are wrapped
 * as one version effective since the beginning of time, so nothing recorded
 * against them changes meaning: `dosage` becomes the strength (that is what it
 * always held), the archived flag and the old Inprogress/Stopped/Completed
 * lifecycle become the version's status.
 */
export const normalizeMedicine = (med, legacyHistory = []) => {
    if (!med || med.id == null) return null;
    if (Array.isArray(med.versions) && med.versions.length) {
        return {
            id: med.id,
            versions: med.versions.map((v) => ({
                ...EMPTY_VERSION,
                ...v,
                times: normalizeTimeList(v.times),
                days: Array.isArray(v.days) ? v.days : [...DAYS],
                supersedesVersions: Array.isArray(v.supersedesVersions) ? v.supersedesVersions : [],
            })),
            changes: Array.isArray(med.changes) ? med.changes : [],
        };
    }

    const lifecycle = legacyHistory.find((h) => h && String(h.id) === String(med.id)) || {};
    const times = medTimes(med);
    const status = (med.archived || med.archived_at)
        ? 'archived'
        : (STATUS_FOR_LIFECYCLE[lifecycle.status] || 'active');

    return {
        id: med.id,
        versions: [{
            ...EMPTY_VERSION,
            name: med.name || '',
            strength: med.strength || med.dosage || '',
            dose: med.dose || '',
            route: med.route || '',
            category: med.category || '',
            instruction: med.instruction || '',
            frequency: med.frequency || frequencyFromTimes(times),
            status,
            times,
            days: Array.isArray(med.days) && med.days.length ? med.days : [...DAYS],
            start: String(lifecycle.date || '').slice(0, 10) || '1970-01-01',
            end: med.end || '',
            doctor: med.doctor || '',
            notes: med.notes || '',
            prn: med.prn || null,
            effectiveFrom: '1970-01-01T00:00',
            supersedesVersions: [],
        }],
        changes: [],
    };
};

export const normalizePlan = (plan, legacyHistory = []) =>
    parsePlan(plan).map((m) => normalizeMedicine(m, parsePlan(legacyHistory))).filter(Boolean);

// --- version resolution ----------------------------------------------------

/**
 * The versions that still count, oldest first. Saving a change while an earlier
 * one is still pending supersedes it: both stay on the record (History shows
 * them) but only the surviving chain drives the schedule.
 */
export const resolvedVersions = (med) => {
    const versions = (med && med.versions) || [];
    const superseded = new Set(versions.flatMap((v) => v.supersedesVersions || []));
    return versions
        .map((version, index) => ({ version, index }))
        .filter((entry) => !superseded.has(entry.index))
        .sort((a, b) =>
            String(a.version.effectiveFrom).localeCompare(String(b.version.effectiveFrom))
            || a.index - b.index);
};

/** The version in force at `stamp` ('YYYY-MM-DDTHH:mm'). */
export const versionAt = (med, stamp) => {
    const resolved = resolvedVersions(med);
    if (!resolved.length) return null;
    const match = resolved.filter((e) => String(e.version.effectiveFrom) <= stamp).at(-1);
    return (match || resolved[0]).version;
};

export const currentVersion = (med, now = nowStamp()) => versionAt(med, now);
export const latestVersion = (med) => (resolvedVersions(med).at(-1) || {}).version || null;
export const pendingVersions = (med, now = nowStamp()) =>
    resolvedVersions(med).filter((e) => String(e.version.effectiveFrom) > now).map((e) => e.version);

/** The indexes a new version has to supersede to replace the pending updates. */
export const pendingIndexes = (med, now = nowStamp()) =>
    resolvedVersions(med).filter((e) => String(e.version.effectiveFrom) > now).map((e) => e.index);

export const isMedArchived = (med, now = nowStamp()) => {
    const v = currentVersion(med, now);
    return !!v && v.status === 'archived';
};

// --- schedule --------------------------------------------------------------

/** Is this version due at `date` `time`? PRN medicines are never auto-scheduled. */
export const isScheduled = (version, date, time) => {
    if (!version || version.status !== 'active' || version.frequency === 'prn') return false;
    if (version.start && date < version.start) return false;
    if (version.end && date > version.end) return false;
    const days = Array.isArray(version.days) ? version.days : [];
    if (days.length && !days.includes(dayName(date))) return false;
    return Array.isArray(version.times) && version.times.includes(time);
};

export const isPrn = (version) => !!version && version.frequency === 'prn';

export const courseText = (v) => {
    if (!v) return '';
    if (!v.start) return 'No course dates';
    return v.end ? `${fmtDate(v.start)} – ${fmtDate(v.end)}` : `From ${fmtDate(v.start)} · Ongoing`;
};

export const scheduleText = (v) => {
    if (!v) return '';
    if (v.frequency === 'prn') return 'As required';
    const days = Array.isArray(v.days) ? v.days : [];
    if (!days.length || days.length === 7) return 'Every day';
    return days
        .filter((d) => DAYS.includes(d))
        .sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b))
        .join(', ');
};

/** [key, label] for the status pill on a medication card. */
export const statusInfo = (v, today = todayISO()) => {
    if (!v) return ['ended', 'Unknown'];
    if (v.status === 'archived') return ['archived', 'Archived'];
    if (v.end && v.end < today) return ['ended', 'Scheduled course ended'];
    if (v.status === 'hold') return ['hold', 'On hold'];
    if (v.start && v.start > today) return ['upcoming', `Starts ${fmtDate(v.start)}`];
    return ['active', 'Active'];
};

// --- doses -----------------------------------------------------------------

export const OUTCOME_LABELS = {
    given: 'Given', refused: 'Refused', withheld: 'Withheld',
    omitted: 'Not available', unrecorded: 'No record', scheduled: 'Scheduled',
};

export const OUTCOME_SHORT = {
    given: 'G', refused: 'R', withheld: 'W', omitted: 'NA', unrecorded: 'NR', scheduled: 'S',
};

export const OUTCOME_ORDER = ['given', 'refused', 'withheld', 'omitted', 'unrecorded', 'scheduled'];

/** A recorded status ('Given', 'Not available'...) -> the outcome key shown here. */
export const outcomeOf = (status) => {
    switch (String(status || '').toLowerCase()) {
        case 'given': return 'given';
        case 'refused': return 'refused';
        case 'withheld': return 'withheld';
        case 'not available': return 'omitted';
        default: return 'unrecorded';
    }
};

export const doseKeyOf = (rec) => `${rec.plan_id}|${rec.dose_date}|${rec.scheduled_time || ''}`;

/** Every entry made against one dose, oldest first — the last one stands. */
export const entriesForDose = (records, key) =>
    records
        .filter((r) => doseKeyOf(r) === key)
        .slice()
        .sort((a, b) => String(a.recorded_at).localeCompare(String(b.recorded_at)) || a.id - b.id);

/**
 * Everything due (or recorded) on one date: the schedule that was in force
 * that day, with any recorded outcome laid over the top.
 */
export const dayDoses = (meds, records, date, medicineFilter = 'all', now = nowStamp()) => {
    const doses = new Map();
    const wanted = meds.filter((m) => medicineFilter === 'all' || String(m.id) === String(medicineFilter));

    wanted.forEach((med) => {
        const times = [...new Set(resolvedVersions(med).flatMap((e) => e.version.times || []))];
        times.forEach((time) => {
            const version = versionAt(med, `${date}T${time}`);
            if (!isScheduled(version, date, time)) return;
            const key = `${med.id}|${date}|${time}`;
            doses.set(key, {
                key,
                medicineId: med.id,
                date,
                time,
                version,
                outcome: `${date}T${time}` > now ? 'scheduled' : 'unrecorded',
                record: null,
                entries: 0,
            });
        });
    });

    records
        .filter((r) => r.dose_date === date
            && (medicineFilter === 'all' || String(r.plan_id) === String(medicineFilter)))
        .forEach((r) => {
            const key = doseKeyOf(r);
            if (doses.has(key) && doses.get(key).record) return;
            const entries = entriesForDose(records, key);
            const latest = entries.at(-1);
            const med = meds.find((m) => String(m.id) === String(latest.plan_id));
            doses.set(key, {
                key,
                medicineId: latest.plan_id,
                date: latest.dose_date,
                time: latest.scheduled_time || '',
                version: med ? versionAt(med, `${latest.dose_date}T${latest.scheduled_time || '23:59'}`) : null,
                outcome: outcomeOf(latest.status),
                record: latest,
                entries: entries.length,
            });
        });

    return [...doses.values()].sort((a, b) =>
        String(a.time).localeCompare(String(b.time))
        || String((a.version || {}).name || '').localeCompare(String((b.version || {}).name || '')));
};

export const countOutcomes = (doses) => {
    const counts = {};
    doses.forEach((d) => { counts[d.outcome] = (counts[d.outcome] || 0) + 1; });
    return counts;
};

/** PRN medicines available on a date — never counted as scheduled doses. */
export const prnAvailable = (meds, date, medicineFilter = 'all') =>
    meds
        .filter((m) => medicineFilter === 'all' || String(m.id) === String(medicineFilter))
        .map((m) => versionAt(m, `${date}T23:59`))
        .filter((v) => v && isPrn(v) && v.status === 'active'
            && (!v.start || date >= v.start) && (!v.end || date <= v.end));

// --- change preview --------------------------------------------------------

/**
 * The first dose still to be given from `from` onwards. Only looks a week past
 * each schedule anchor — far enough to catch a weekly medicine, cheap enough to
 * re-run on every keystroke in the form.
 */
export const nextScheduled = (med, from, records = [], onlyVersion = null) => {
    const versions = resolvedVersions(med).map((e) => e.version);
    if (!versions.length) return null;

    const anchors = new Set([from.slice(0, 10)]);
    versions.forEach((v) => {
        if (v.start) anchors.add(v.start);
        if (v.effectiveFrom) anchors.add(v.effectiveFrom.slice(0, 10));
    });

    const dates = new Set();
    [...anchors]
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
        .forEach((anchor) => {
            const start = anchor < from.slice(0, 10) ? from.slice(0, 10) : anchor;
            for (let offset = 0; offset < 8; offset += 1) {
                const date = parseDate(start);
                date.setUTCDate(date.getUTCDate() + offset);
                dates.add(dateISO(date));
            }
        });

    const times = [...new Set(versions.flatMap((v) => v.times || []))]
        .filter((t) => /^\d{2}:\d{2}$/.test(t))
        .sort();
    const recorded = new Set(records.map(doseKeyOf));

    for (const date of [...dates].sort()) {
        for (const time of times) {
            if (`${date}T${time}` < from) continue;
            const version = versionAt(med, `${date}T${time}`);
            if (onlyVersion && version !== onlyVersion) continue;
            if (isScheduled(version, date, time) && !recorded.has(`${med.id}|${date}|${time}`)) {
                return { date, time, stamp: `${date}T${time}`, version };
            }
        }
    }
    return null;
};

/** The most recent dose actually given, up to `now`. */
export const lastGiven = (records, medicineId, now = nowStamp()) => {
    const latest = new Map();
    records
        .filter((r) => String(r.plan_id) === String(medicineId))
        .forEach((r) => latest.set(doseKeyOf(r), entriesForDose(records, doseKeyOf(r)).at(-1)));
    return [...latest.values()]
        .filter((r) => r && outcomeOf(r.status) === 'given'
            && `${r.dose_date}T${r.scheduled_time || '00:00'}` <= now)
        .sort((a, b) => `${b.dose_date}T${b.scheduled_time || ''}`
            .localeCompare(`${a.dose_date}T${a.scheduled_time || ''}`))[0] || null;
};

// --- change log ------------------------------------------------------------

const FIELD_LABELS = {
    name: 'Medicine name', strength: 'Strength & form', dose: 'Dose to give', route: 'Route',
    category: 'Condition / category', instruction: 'Instruction', frequency: 'Frequency',
    status: 'Status', times: 'Administration times', days: 'Days', start: 'Start date',
    end: 'End date', effectiveFrom: 'Change takes effect from', doctor: 'Prescribing doctor',
    notes: 'Notes', prn: 'As-required instructions',
};

const FREQUENCY_LABELS = {
    once: 'Once daily', twice: 'Twice daily', three: 'Three times daily',
    four: 'Four times daily', weekly: 'Weekly', custom: 'Custom', prn: 'As required (PRN)',
};

const STATUS_LABELS = { active: 'Active', hold: 'On hold', archived: 'Archived' };

const fieldValue = (key, value) => {
    if (value == null || value === '' || (Array.isArray(value) && !value.length)) {
        return key === 'end' ? 'Ongoing' : 'Not set';
    }
    if (key === 'days' || key === 'times') return Array.isArray(value) ? value.join(', ') : String(value);
    if (key === 'start' || key === 'end') return fmtDate(value);
    if (key === 'effectiveFrom') return fmtStamp(value);
    if (key === 'frequency') return FREQUENCY_LABELS[value] || String(value);
    if (key === 'status') return STATUS_LABELS[value] || String(value);
    if (key === 'prn') {
        return `Minimum interval: ${value.interval || '—'} hours; maximum in 24 hours: ${value.maximum || '—'}; when to give: ${value.when || '—'}`;
    }
    return String(value);
};

/** What actually changed between two versions, ready for the change log. */
export const changedFields = (before, after) =>
    Object.keys(FIELD_LABELS)
        .filter((k) => JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null))
        .map((k) => ({
            label: FIELD_LABELS[k],
            before: fieldValue(k, before[k]),
            after: fieldValue(k, after[k]),
        }));

export const versionSummary = (v) => [
    v.strength, v.dose, v.route,
    v.frequency === 'prn' ? 'As required' : `${(v.times || []).join(', ')} · ${scheduleText(v)}`,
    courseText(v),
    statusInfo(v)[1],
].filter(Boolean).join(' / ');

/** Every change ever made, newest first, across the whole plan. */
export const allChanges = (meds, medicineFilter = 'all', date = '') =>
    meds
        .filter((m) => medicineFilter === 'all' || String(m.id) === String(medicineFilter))
        .flatMap((m) => (m.changes || []).map((c) => ({ ...c, medicineId: m.id })))
        .filter((c) => !date || String(c.at).slice(0, 10) === date)
        .sort((a, b) => String(b.at).localeCompare(String(a.at)));

export const newId = () => Date.now() + Math.floor(Math.random() * 1000);
